import { Card } from "primereact/card";
import PropTypes from "prop-types";

/** Components */
import Badges from "../../components/common/badges/Badges";
import NoRecordFound from "../../components/common/no-record-found/NoRecordFound";

/** RTK Query */
import { useGetDataQuery } from "../../app/services/commonApi";

/** CSS */
import "../talentCrm/TalentCrm.scss";

const METRIC_DEFS = [
  { key: "candidateCount", label: "Candidates", icon: "pi-users", tone: "orange" },
  { key: "profileCount", label: "CRM Profiles", icon: "pi-id-card", tone: "blue" },
  { key: "activePoolCount", label: "Active Pools", icon: "pi-sitemap", tone: "purple" },
  { key: "poolMembershipCount", label: "Pool Members", icon: "pi-user-plus", tone: "blue" },
  { key: "tagCount", label: "Tags", icon: "pi-tags", tone: "dark" },
  {
    key: "eligibleForRediscoveryCount",
    label: "Rediscovery Eligible",
    icon: "pi-search",
    tone: "green",
  },
  {
    key: "withdrawnConsentCount",
    label: "Consent Restricted",
    icon: "pi-ban",
    tone: "red",
  },
  {
    key: "engagementCountLast30Days",
    label: "Engagements (30d)",
    icon: "pi-comments",
    tone: "orange",
  },
  { key: "freshRediscoveryCacheCount", label: "Cache Fresh", icon: "pi-check-circle", tone: "green" },
  { key: "staleRediscoveryCacheCount", label: "Cache Stale", icon: "pi-clock", tone: "red" },
];

const MetricCard = ({ label, value, icon, tone }) => (
  <div className="talent-metric-card">
    <div className={`talent-metric-icon icon-${tone}`}>
      <i className={`pi ${icon}`} />
    </div>
    <div className="talent-metric-body">
      <span className="label">{label}</span>
      <span className="value">{value ?? 0}</span>
    </div>
  </div>
);

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  icon: PropTypes.string.isRequired,
  tone: PropTypes.string.isRequired,
};

const SectionEmpty = ({ message }) => (
  <div className="talent-section-empty">
    <i className="pi pi-inbox" />
    <p>{message}</p>
  </div>
);

SectionEmpty.propTypes = {
  message: PropTypes.string.isRequired,
};

const TalentCrmReport = () => {
  const { data, isFetching, isError } = useGetDataQuery({
    url: "/Report/GetTalentCrmReport",
  });

  const report = data?.data;
  const metrics = report?.metrics;

  const metricValues = {
    candidateCount: metrics?.candidateCount,
    profileCount: metrics?.profileCount,
    activePoolCount: metrics?.activePoolCount,
    poolMembershipCount: metrics?.poolMembershipCount,
    tagCount: metrics?.tagCount,
    eligibleForRediscoveryCount: metrics?.eligibleForRediscoveryCount,
    withdrawnConsentCount: metrics?.withdrawnConsentCount,
    engagementCountLast30Days: metrics?.engagementCountLast30Days,
    freshRediscoveryCacheCount: report?.freshRediscoveryCacheCount,
    staleRediscoveryCacheCount: report?.staleRediscoveryCacheCount,
  };

  if (isError) {
    return <NoRecordFound message="Unable to load Talent CRM report" />;
  }

  return (
    <Card className="custom-card talent-crm-metrics-page">
      <div className="page-header">
        <div className="left-title-part">
          <h2 className="card-title">Talent CRM Report</h2>
          <p className="page-subtitle">
            Consent health, engagement trend, pools, and rediscovery cache status.
          </p>
        </div>
        {isFetching ? <span className="muted">Loading…</span> : null}
      </div>

      {!metrics ? (
        <NoRecordFound message="No report data available" />
      ) : (
        <>
          <div className="talent-metrics-grid">
            {METRIC_DEFS.map((def) => (
              <MetricCard
                key={def.key}
                label={def.label}
                value={metricValues[def.key]}
                icon={def.icon}
                tone={def.tone}
              />
            ))}
          </div>

          <div className="talent-report-panels">
            <section className="talent-crm-section">
              <div className="section-heading">
                <i className="pi pi-shield" />
                <h3>Consent health</h3>
              </div>
              {(report.consentHealth || []).length === 0 ? (
                <SectionEmpty message="No consent rows yet" />
              ) : (
                <ul className="history-list">
                  {report.consentHealth.map((row) => (
                    <li key={row.eligibilityStatus}>
                      <strong>{row.eligibilityStatus}</strong>
                      <span className="count-pill">{row.count}</span>
                      <span />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="talent-crm-section">
              <div className="section-heading">
                <i className="pi pi-chart-line" />
                <h3>Engagement trend (14d)</h3>
              </div>
              {(report.engagementTrend || []).length === 0 ? (
                <SectionEmpty message="No engagements in the last 14 days" />
              ) : (
                <ul className="history-list">
                  {report.engagementTrend.map((row) => (
                    <li key={row.dayUtc}>
                      <strong>{String(row.dayUtc).slice(0, 10)}</strong>
                      <span className="count-pill">{row.count}</span>
                      <span />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="talent-crm-section">
              <div className="section-heading">
                <i className="pi pi-sitemap" />
                <h3>Pools</h3>
              </div>
              {(report.pools || []).length === 0 ? (
                <SectionEmpty message="No pools yet" />
              ) : (
                <ul className="history-list">
                  {report.pools.map((p) => (
                    <li key={p.talentPoolId}>
                      <strong>{p.name}</strong>
                      <span>{p.memberCount ?? 0} members</span>
                      <Badges
                        label={p.isActive ? "Active" : "Inactive"}
                        type={p.isActive ? "success" : "error"}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </Card>
  );
};

export default TalentCrmReport;
